import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE, getRouteConfig } from "./seo.config";

interface SEOProps {
  path?: string;
  title?: string;
  description?: string;
  canonical?: string;
  keywords?: string[];
  image?: string;
  type?: string;
}

export function SEO({
  path,
  title,
  description,
  canonical,
  keywords,
  image = "/og-preview.png",
  type = "website",
}: SEOProps) {
  const location = useLocation();
  const currentPath = path || location.pathname;
  const routeConfig = getRouteConfig(currentPath);

  // Use provided props or fall back to route config
  const finalTitle = title || routeConfig.title;
  const finalDescription =
    description || routeConfig.description || SITE.description;
  const finalCanonical = canonical || routeConfig.canonical;
  const finalKeywords = keywords || routeConfig.keywords || [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      {finalDescription && (
        <meta name="description" content={finalDescription} />
      )}
      {finalKeywords.length > 0 && (
        <meta name="keywords" content={finalKeywords.join(", ")} />
      )}
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}

      {/* Open Graph Tags */}
      <meta property="og:title" content={finalTitle} />
      {finalDescription && (
        <meta property="og:description" content={finalDescription} />
      )}
      <meta property="og:type" content={type} />
      {finalCanonical && <meta property="og:url" content={finalCanonical} />}
      <meta property="og:image" content={`${SITE.prodUrl}${image}`} />
      <meta property="og:site_name" content={SITE.name} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      {finalDescription && (
        <meta name="twitter:description" content={finalDescription} />
      )}
      <meta name="twitter:image" content={`${SITE.prodUrl}${image}`} />
      {SITE.twitter && <meta name="twitter:site" content={SITE.twitter} />}

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content={SITE.name} />
      <meta name="theme-color" content="#8b5cf6" />

      {/* Structured Data - Only on homepage */}
      {currentPath === "/" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: SITE.name,
            url: SITE.prodUrl,
            jobTitle: "Software Engineering Executive",
            description:
              "Engineering leader specializing in AI-native automation, cloud infrastructure, and metrics.",
            sameAs: [SITE.github, SITE.linkedin].filter(Boolean),
          })}
        </script>
      )}

      {/* Website Schema - Only on homepage */}
      {currentPath === "/" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "thebrianjones.dev",
            url: SITE.prodUrl,
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE.prodUrl}/search?q={query}`,
              "query-input": "required name=query",
            },
          })}
        </script>
      )}
    </Helmet>
  );
}

